import type {
  FhResultLike,
  FhVitalsClient,
  FhVitalsInitArgs,
  FhVitalsMeasureArgs,
} from './fh-vitals-types'

const SDK_SCRIPT_SRC = '/sdk/fhvitals_v2/fhvitals_sdk.js'

declare global {
  interface Window {
    FHVitalsSDK?: {
      version?: string
      init(args: FhVitalsInitArgs): Promise<FhResultLike>
      startPreview(canvasId: string, facingMode: string): Promise<FhResultLike>
      stopPreview(): Promise<FhResultLike>
      startMeasuring(args: FhVitalsMeasureArgs): Promise<FhResultLike>
      stopMeasuring(): Promise<FhResultLike>
    }
    __VIDEO_URL?: string
  }
}

/**
 * Whether this build carries the camera-replacement shim. Replaced with a literal `true` or `false`
 * at build time by the define in vite.config.ts, which is what lets Rollup remove the branch and
 * everything only it reaches.
 */
declare const __FH_CAMERA_REPLACEMENT__: boolean

let scriptLoad: Promise<void> | null = null

function loadSdkScript(): Promise<void> {
  if (window.FHVitalsSDK) {
    return Promise.resolve()
  }
  scriptLoad ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SDK_SCRIPT_SRC
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${SDK_SCRIPT_SRC}`))
    document.head.appendChild(script)
  })
  return scriptLoad
}

/**
 * The SDK creates its own internal <video> element (Camera._webcam) and calls play() on it after
 * attaching the camera stream. A browser's autoplay policy rejects play() on an unmuted video
 * without a user gesture, and the SDK maps that straight to CAMERA_OPEN_FAILED — indistinguishable
 * from an actual hardware failure unless you already know this trap (faceheart2.html; task brief).
 * Patching document.createElement so every <video> made from here on is born muted is the only
 * point of control available: the SDK never exposes the element it creates.
 */
function patchVideoAutoplay(): void {
  const flagged = '__fhVideoAutoplayPatched'
  const globalWithFlag = window as unknown as Record<string, boolean>
  if (globalWithFlag[flagged]) {
    return
  }
  globalWithFlag[flagged] = true

  const original = document.createElement.bind(document)
  document.createElement = ((tagName: string, options?: ElementCreationOptions) => {
    const element = original(tagName, options)
    if (typeof tagName === 'string' && tagName.toLowerCase() === 'video') {
      const video = element as HTMLVideoElement
      video.muted = true
      video.defaultMuted = true
      video.setAttribute('muted', '')
      video.setAttribute('playsinline', '')
    }
    return element
  }) as typeof document.createElement
}

/**
 * Streams a recorded measurement video through getUserMedia instead of a real camera, when
 * `window.__VIDEO_URL` is set. Kept from faceheart2.html deliberately (task brief: "a real
 * technique for a deterministic scan and is worth keeping as a test affordance") — it is how a
 * Playwright journey can drive the actual vendor SDK end to end against fixed footage without a
 * camera or a person in front of one, while still never touching the vendor's license server in a
 * *unit* test (that seam is FhVitalsClient itself; this one is for journeys willing to pay the SDK's
 * real cost).
 *
 * IT IS COMPILED OUT OF A PRODUCTION BUILD, and it has to be. The affordance and the attack are the
 * same code: shipped to a patient's browser it is a way to feed chosen vitals into a clinical
 * record over the real token, and the service has no way to tell the result from a scan that
 * happened. `__FH_CAMERA_REPLACEMENT__` is a Vite define — false in `vite build`, true in dev and in
 * `--mode journey` — so Rollup drops the call site and then this function, and
 * scripts/check-web-bundle.sh asserts its absence in the built artefact rather than trusting the
 * flag.
 */
async function shimGetUserMediaFromVideo(videoUrl: string): Promise<void> {
  const video = document.createElement('video')
  video.src = videoUrl
  video.loop = true
  video.muted = true
  video.playsInline = true
  video.style.cssText = 'position:fixed;top:-9999px;width:640px;height:480px'
  document.body.appendChild(video)

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve()
    video.onerror = () => reject(new Error('Video load failed for __VIDEO_URL'))
  })
  await video.play()

  const stream = (video as HTMLVideoElement & { captureStream(): MediaStream }).captureStream()
  navigator.mediaDevices.getUserMedia = (() => Promise.resolve(stream)) as typeof navigator.mediaDevices.getUserMedia
}

/** The production FhVitalsClient: the real browser SDK, loaded once, patched once. */
export function createBrowserFhVitalsClient(): FhVitalsClient {
  return {
    async init(args) {
      patchVideoAutoplay()
      if (__FH_CAMERA_REPLACEMENT__ && window.__VIDEO_URL) {
        await shimGetUserMediaFromVideo(window.__VIDEO_URL)
      }
      await loadSdkScript()
      return window.FHVitalsSDK!.init(args)
    },
    startPreview(canvasId, facingMode) {
      return window.FHVitalsSDK!.startPreview(canvasId, facingMode)
    },
    stopPreview() {
      return window.FHVitalsSDK!.stopPreview()
    },
    startMeasuring(args: FhVitalsMeasureArgs) {
      return window.FHVitalsSDK!.startMeasuring(args)
    },
    stopMeasuring() {
      return window.FHVitalsSDK!.stopMeasuring()
    },
  }
}
